import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { 
  ChevronLeft, 
  ChevronRight, 
  Instagram, 
  Eye, 
  Clock, 
  Heart,
  MessageCircle,
  X,
  Share2,
  Calendar,
  Clock3,
  Loader2,
  Download,
  Play,
  Pause,
  Check
} from 'lucide-react';
import TiktokIcon from '@/components/ui/TiktokIcon';
import { sendRequest } from "../api";
import ProcessingModal from './ProcessingModal';
import { cn } from '@/lib/utils';

export default function CrossPostView({ video: initialVideo, onClose, user, platforms, onError, reloadUser }) {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const videos = Array.isArray(initialVideo) ? initialVideo : [initialVideo];
  const video = videos[currentVideoIndex];
  
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showScheduleSheet, setShowScheduleSheet] = useState(false);
  const [scheduleType, setScheduleType] = useState('now'); // 'now', 'best-time', 'custom'
  const [isPosting, setIsPosting] = useState(false);
  const [scheduledTime, setScheduledTime] = useState('');
  const [scheduleError, setScheduleError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [downloadId, setDownloadId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);
  const [playbackError, setPlaybackError] = useState(null);
  const [showControls, setShowControls] = useState(true);
  const [isHovering, setIsHovering] = useState(false);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [isFetchingBestTime, setIsFetchingBestTime] = useState(false);
  const [bestTime, setBestTime] = useState(null);
  const [postingProgress, setPostingProgress] = useState(0);
  const videoRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  const formatNumber = (count) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  const handlePost = async (schedule = 'now', time = null) => {
    setIsPosting(true);
    setScheduleError(null);
    try {
      await sendRequest("/videos", "POST", {
        source: video.platform,
        target: selectedPlatform,
        target_account: selectedAccount,
        source_id: video.id,
        schedule,
        time: time ? new Date(time).getTime() : null
      });
      
      setScheduleError(null);
      await reloadUser?.();
      
      // If there are more videos to process, move to the next one
      if (currentVideoIndex < videos.length - 1) {
        setCurrentVideoIndex(prev => prev + 1);
        setSelectedPlatform(null);
        setSelectedAccount(null);
        setShowScheduleSheet(false);
        setPostingProgress((currentVideoIndex + 1) / videos.length * 100);
      } else {
        // All videos processed
        onClose({ 
          type: 'success',
          message: `Successfully cross-posted ${videos.length} video${videos.length > 1 ? 's' : ''} to ${selectedPlatform}.`
        });
      }
    } catch (error) {
      onError?.(error.message);
      setScheduleError(error.message);
    } finally {
      setIsPosting(false);
    }
  };

  const handlePlatformSelect = (platform) => {
    setSelectedPlatform(platform);
    // If there's only one account for the platform, select it automatically
    const platformAccounts = platforms[platform]?.accounts || [];
    if (platformAccounts.length === 1) {
      setSelectedAccount(platformAccounts[0].uid);
    }
    setShowScheduleSheet(true);
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    setDownloadError(null);
    
    try {
      // Ensure we have a valid share_url
      if (!video.share_url) {
        throw new Error("No valid video URL found");
      }

      // Clean the URL by removing query parameters
      const cleanUrl = video.share_url.split('?')[0];
      
      const { download_id, url } = await sendRequest("/downloads", "POST", {
        video_urls: [cleanUrl],
        uid: video.uid,
        platform: video.platform,
      });

      if (download_id) {
        setDownloadId(download_id);
      } else {
        window.location.href = url;
      }
    } catch (error) {
      onError?.(error.message);
      setDownloadError(error.message);
      setIsDownloading(false);
    } 
  };

  const getVideoUrl = async () => {
    setIsLoadingVideo(true);
    setPlaybackError(null);
    try {
      const { url } = await sendRequest(`/videos/${video.platform}/${video.id}`, "GET", {
        uid: video.uid
      });
      setVideoUrl(url);
      // Wait for state update to complete
      await new Promise(resolve => setTimeout(resolve, 0));
      if (videoRef.current) {
        try {
          await videoRef.current.play();
          setIsPlaying(true);
        } catch (error) {
          setPlaybackError("Failed to play video. Please try again.");
          setIsPlaying(false);
        }
      }
    } catch (error) {
      setPlaybackError("Failed to load video. Please try again.");
      setIsPlaying(false);
      onError?.(error.message);
    } finally {
      setIsLoadingVideo(false);
    }
  };

  const handleVideoClick = () => {
    if (!videoUrl) {
      getVideoUrl();
      return;
    }
    if (isPlaying) {
      videoRef.current?.pause();
    } else {
      setPlaybackError(null);
      videoRef.current?.play().catch((error) => {
        setPlaybackError("Failed to play video. Please try again.");
        setIsPlaying(false);
      });
    }
  };

  useEffect(() => {
    if ((isPlaying || !isHovering) && !playbackError) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 1000);
    } else {
      setShowControls(true);
    }

    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [isPlaying, isHovering, playbackError]);

  useEffect(() => {
    // Cleanup function to pause video when component unmounts
    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
      }
    };
  }, []);

  // Add escape key handler
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        if (showScheduleSheet) {
          setShowScheduleSheet(false);
          setSelectedPlatform(null);
          setSelectedAccount(null);
        } else {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showScheduleSheet, onClose]);

  const handleScheduleTypeChange = async (type) => {
    setScheduleType(type);
    // Clear errors when switching tabs
    setScheduleError(null);
    
    if (type === 'best-time' && (!bestTime || scheduleError)) {
      setIsFetchingBestTime(true);
      setBestTime(null);
      try {
        const response = await sendRequest("/metrics/best-time", "GET");
        if (response.error) {
          throw new Error(response.error);
        }
        const bestHour = Object.entries(response.best_times)
          .sort((a, b) => b[1].average_engagement - a[1].average_engagement)[0];
        
        if (bestHour) {
          // Set time to today at the best hour
          const today = new Date();
          today.setHours(parseInt(bestHour[0]), 0, 0, 0);
          setBestTime(today);
          setScheduledTime(today.toISOString().slice(0, 16));
        }
      } catch (error) {
        setScheduleError(error.message);
        setBestTime(null);
      } finally {
        setIsFetchingBestTime(false);
      }
    }
  };

  if (!video) {
    return null;
  }

  const platformIcons = {
    instagram: <Instagram className="h-4 w-4" />,
    tiktok: <TiktokIcon />
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="max-w-5xl mx-auto bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                onClick={() => {
                  if (showScheduleSheet) {
                    setShowScheduleSheet(false);
                    setSelectedPlatform(null);
                    setSelectedAccount(null);
                  } else {
                    onClose();
                  }
                }}
                className="text-gray-600 hover:text-gray-900"
              >
                {showScheduleSheet ? (
                  <>
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    Back
                  </>
                ) : (
                  <>
                    <X className="h-4 w-4 mr-2" />
                    Exit
                  </>
                )}
              </Button>
              <h2 className="text-xl font-semibold">
                {showScheduleSheet ? (
                  <div className="flex items-center gap-2">
                    {selectedPlatform === 'instagram' ? (
                      <Instagram className="h-4 w-4 text-pink-500" />
                    ) : (
                      <TiktokIcon />
                    )}
                    Schedule Post
                  </div>
                ) : (
                  <>
                    Cross-Post Video {videos.length > 1 ? `(${currentVideoIndex + 1}/${videos.length})` : ''}
                  </>
                )}
              </h2>
            </div>
            {videos.length > 1 && !showScheduleSheet && (
              <div className="w-48">
                <div className="h-1 bg-gray-200 rounded-full">
                  <div 
                    className="h-full bg-purple-500 rounded-full transition-all duration-300"
                    style={{ width: `${postingProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-col md:flex-row gap-6">
          {/* Video Preview */}
          <div className="w-full md:w-[320px] flex-shrink-0 space-y-4">
            <Card className="backdrop-blur-sm bg-white/60 dark:bg-gray-900/50 border border-white/20">
              <CardContent className="p-0">
                <div className="relative">
                  <div className="pb-[177.77%]">
                    {video.share_url ? (
                      <>
                        <video
                          ref={videoRef}
                          src={videoUrl}
                          poster={video.thumbnail || video.cover_image_url}
                          className="absolute inset-0 w-full h-full object-cover rounded-t-lg"
                          playsInline
                          loop
                          onPlay={() => {
                            setIsPlaying(true);
                            setShowControls(true);
                          }}
                          onPause={() => {
                            setIsPlaying(false);
                            setShowControls(true);
                          }}
                          onError={() => {
                            setPlaybackError("Failed to play video. Please try again.");
                            setIsPlaying(false);
                            setShowControls(true);
                          }}
                          onClick={handleVideoClick}
                        />
                        <button
                          className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-black/0 via-black/0 to-black/60"
                          onClick={handleVideoClick}
                          onMouseEnter={() => setIsHovering(true)}
                          onMouseLeave={() => setIsHovering(false)}
                        >
                          <div className="absolute inset-0 flex items-center justify-center">
                            {isLoadingVideo ? (
                              <div className="bg-black/40 p-4 rounded-full">
                                <Loader2 className="h-12 w-12 text-white animate-spin" />
                              </div>
                            ) : (
                              <div className={`
                                transition-all duration-200 
                                ${showControls || isHovering ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}
                              `}>
                                <div className="bg-black/40 p-4 rounded-full backdrop-blur-sm">
                                  {isPlaying && !playbackError ? (
                                    <Pause className="w-12 h-12 text-white" />
                                  ) : (
                                    <Play className="w-12 h-12 text-white" />
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          {playbackError && (
                            <div className="absolute bottom-4 left-4 right-4">
                              <div className="bg-red-500/90 backdrop-blur-sm text-white text-sm px-3 py-2 rounded-lg">
                                {playbackError}
                              </div>
                            </div>
                          )}
                        </button>
                      </>
                    ) : (
                      <img
                        src={video.thumbnail || video.cover_image_url}
                        alt={`Video ${video.id}`}
                        className="absolute inset-0 w-full h-full object-cover rounded-t-lg"
                      />
                    )}
                  </div>

                  {/* Platform badges */}
                  <div className="absolute top-2 left-2 flex gap-1">
                    <Badge className="backdrop-blur-md bg-black/30 border border-white/20 text-white px-1.5 py-0.5 rounded-full flex items-center gap-1">
                      {video.platform === 'instagram' ? (
                        <Instagram className="h-3 w-3" />
                      ) : (
                        <TiktokIcon className="h-3 w-3" />
                      )}
                      <span className="text-xs">{video.platform}</span>
                    </Badge>
                  </div>

                  {/* Downloaded badge */}
                  {video.is_downloaded && (
                    <div className="absolute bottom-2 right-2 group">
                      <span className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-2 py-1 bg-black/90 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        Backed Up
                      </span>
                      <Badge 
                        className="
                          backdrop-blur-md bg-green-500/30 border border-green-400/30
                          text-white text-[10px] sm:text-xs
                          px-2 py-1 rounded-full
                          flex items-center gap-1.5
                          shadow-lg shadow-green-500/30
                        "
                      >
                        <Check className="h-3 w-3" />
                      </Badge>
                    </div>
                  )}

                  {/* Stats overlay */}
                  <div className="absolute inset-x-0 bottom-0 backdrop-blur-md bg-gradient-to-t from-black/60 to-black/0 p-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-white/90 text-xs">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1">
                            {video.platform === 'instagram' ? (
                              <>
                                <Heart className="h-3 w-3 text-pink-400" />
                                <span>{formatNumber(video.metrics?.likes || 0)}</span>
                              </>
                            ) : (
                              <>
                                <Eye className="h-3 w-3 text-blue-400" />
                                <span>{formatNumber(video.view_count || 0)}</span>
                              </>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3 text-purple-400" />
                            <span>
                              {(() => {
                                const seconds = Math.round(video.duration || 0);
                                if (seconds >= 60) {
                                  const minutes = Math.floor(seconds/60);
                                  const remainingSeconds = seconds % 60;
                                  return `${minutes}m ${remainingSeconds}s`;
                                }
                                return `${seconds}s`;
                              })()}
                            </span>
                          </div>
                        </div>
                        {video.metrics?.engagement_rate && (
                          <Badge variant="secondary" className="bg-purple-500/20 text-purple-200 border-purple-400/20">
                            {(video.metrics.engagement_rate * 100).toFixed(1)}% Engagement
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Video Info */}
            <div className="space-y-4">
              {/* Download Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
                disabled={isDownloading}
                className="w-full flex items-center justify-center gap-2 bg-white/50 backdrop-blur-sm hover:bg-white/80"
              >
                {isDownloading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    <span>Download HD Video</span>
                  </>
                )}
              </Button>
              {downloadError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-600 text-center">{downloadError}</p>
                </div>
              )}

              {/* Video Requirements */}
              <div className="bg-white/50 backdrop-blur-sm rounded-lg border border-gray-200/50 p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Video Requirements</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Duration</span>
                    <Badge variant="secondary" className={`
                      ${video.duration > 60 ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}
                    `}>
                      {video.duration > 60 ? 'May need trimming' : 'Compatible'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Aspect Ratio</span>
                    <Badge variant="secondary" className="bg-green-100 text-green-700">
                      Compatible
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Resolution</span>
                    <Badge variant="secondary" className="bg-green-100 text-green-700">
                      HD Quality
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Platform Selection and Scheduling */}
          <div className="flex-1">
            {!showScheduleSheet ? (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium mb-2">Choose Platform</h3>
                  <p className="text-sm text-gray-500 mb-4">Select where you want to share this content</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {video.platform !== 'instagram' && (
                    <div className="group relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-pink-500/20 to-purple-500/20 rounded-xl transition-opacity group-hover:opacity-100 opacity-0" />
                      <Button 
                        variant="outline"
                        size="lg"
                        className="relative w-full flex items-start gap-4 h-auto p-4 hover:border-pink-400 transition-all duration-300"
                        onClick={() => handlePlatformSelect('instagram')}
                      >
                        <div className="h-12 w-12 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 flex items-center justify-center">
                          <Instagram className="h-6 w-6 text-white" />
                        </div>
                        <div className="text-left flex-1">
                          <div className="font-medium text-lg mb-1">Instagram</div>
                          <div className="text-sm text-gray-500 mb-3">Share as Reel</div>
                          <div className="flex items-center gap-2 text-xs text-gray-400">
                            <Badge variant="secondary" className="bg-gray-100">60s Max</Badge>
                            <Badge variant="secondary" className="bg-gray-100">9:16 Ratio</Badge>
                          </div>
                        </div>
                      </Button>
                    </div>
                  )}

                  {video.platform !== 'tiktok' && video.platform !== 'instagram' && (
                    <div className="group relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-xl transition-opacity group-hover:opacity-100 opacity-0" />
                      <Button 
                        variant="outline"
                        size="lg"
                        className="relative w-full flex items-start gap-4 h-auto p-4 hover:border-blue-400 transition-all duration-300"
                        onClick={() => handlePlatformSelect('tiktok')}
                      >
                        <div className="h-12 w-12 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
                          <TiktokIcon className="h-6 w-6 text-white" />
                        </div>
                        <div className="text-left flex-1">
                          <div className="font-medium text-lg mb-1">TikTok</div>
                          <div className="text-sm text-gray-500 mb-3">Share as Video</div>
                          <div className="flex items-center gap-2 text-xs text-gray-400">
                            <Badge variant="secondary" className="bg-gray-100">3m Max</Badge>
                            <Badge variant="secondary" className="bg-gray-100">9:16 Ratio</Badge>
                          </div>
                        </div>
                      </Button>
                    </div>
                  )}
                </div>
                
                {/* Platform-specific insights */}
                <div className="mt-8 space-y-4">
                  <h4 className="text-sm font-medium text-gray-600">Platform Insights</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Instagram className="h-4 w-4 text-pink-500" />
                        <span className="text-sm font-medium">Instagram Reels</span>
                      </div>
                      <p className="text-xs text-gray-500">Best for short-form vertical videos with music, effects, and trending sounds. Ideal for creative and engaging content.</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <TiktokIcon className="h-4 w-4" />
                        <span className="text-sm font-medium">TikTok</span>
                      </div>
                      <p className="text-xs text-gray-500">Perfect for trending challenges, music-driven content, and creative effects. Focuses on authentic and entertaining videos.</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Account Selection */}
                {selectedPlatform && platforms[selectedPlatform]?.accounts?.length > 1 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-600 mb-2">Select Account</h3>
                    <div className="grid grid-cols-1 gap-2">
                      {platforms[selectedPlatform].accounts.map((account) => (
                        <button
                          key={account.uid}
                          onClick={() => setSelectedAccount(account.uid)}
                          className={cn(
                            "flex items-center gap-3 p-3 rounded-lg border transition-all",
                            selectedAccount === account.uid
                              ? "border-purple-200 bg-purple-50"
                              : "border-gray-200 bg-white hover:bg-gray-50"
                          )}
                        >
                          <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-gray-200">
                            {account.profile_picture_url ? (
                              <img
                                src={account.profile_picture_url}
                                alt={account.username}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-500">
                                {account.username?.[0]?.toUpperCase()}
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="font-medium">@{account.username}</div>
                            <div className="text-sm text-gray-500">
                              Connected {new Date(account.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          <div className={cn(
                            "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                            selectedAccount === account.uid
                              ? "border-purple-500 bg-purple-500 text-white"
                              : "border-gray-300"
                          )}>
                            {selectedAccount === account.uid && (
                              <Check className="w-3 h-3" />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Schedule Type Selection */}
                <div className="grid grid-cols-3 gap-3">
                  <Button
                    variant={scheduleType === 'now' ? 'default' : 'ghost'}
                    size="lg"
                    className={`
                      relative h-auto px-4 py-6
                      flex flex-col items-center gap-2
                      rounded-xl border transition-all duration-300
                      ${scheduleType === 'now'
                        ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white border-transparent shadow-lg shadow-purple-500/25'
                        : 'hover:border-purple-200 hover:bg-purple-50/50'
                      }
                    `}
                    onClick={() => handleScheduleTypeChange('now')}
                  >
                    <div className={`
                      p-3 rounded-full
                      ${scheduleType === 'now'
                        ? 'bg-white/20'
                        : 'bg-purple-100'
                      }
                    `}>
                      <Share2 className={`h-5 w-5 ${scheduleType === 'now' ? 'text-white' : 'text-purple-600'}`} />
                    </div>
                    <span className={`text-sm font-medium ${scheduleType === 'now' ? 'text-white' : 'text-gray-700'}`}>
                      Post Now
                    </span>
                  </Button>

                  <Button
                    variant={scheduleType === 'best-time' ? 'default' : 'ghost'}
                    size="lg"
                    className={`
                      relative h-auto px-4 py-6
                      flex flex-col items-center gap-2
                      rounded-xl border transition-all duration-300
                      ${scheduleType === 'best-time'
                        ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white border-transparent shadow-lg shadow-purple-500/25'
                        : 'hover:border-purple-200 hover:bg-purple-50/50'
                      }
                    `}
                    onClick={() => handleScheduleTypeChange('best-time')}
                  >
                    <div className={`
                      p-3 rounded-full
                      ${scheduleType === 'best-time'
                        ? 'bg-white/20'
                        : 'bg-purple-100'
                      }
                    `}>
                      <Clock3 className={`h-5 w-5 ${scheduleType === 'best-time' ? 'text-white' : 'text-purple-600'}`} />
                    </div>
                    <span className={`text-sm font-medium ${scheduleType === 'best-time' ? 'text-white' : 'text-gray-700'}`}>
                      Best Time
                    </span>
                  </Button>

                  <Button
                    variant={scheduleType === 'custom' ? 'default' : 'ghost'}
                    size="lg"
                    className={`
                      relative h-auto px-4 py-6
                      flex flex-col items-center gap-2
                      rounded-xl border transition-all duration-300
                      ${scheduleType === 'custom'
                        ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white border-transparent shadow-lg shadow-purple-500/25'
                        : 'hover:border-purple-200 hover:bg-purple-50/50'
                      }
                    `}
                    onClick={() => handleScheduleTypeChange('custom')}
                  >
                    <div className={`
                      p-3 rounded-full
                      ${scheduleType === 'custom'
                        ? 'bg-white/20'
                        : 'bg-purple-100'
                      }
                    `}>
                      <Calendar className={`h-5 w-5 ${scheduleType === 'custom' ? 'text-white' : 'text-purple-600'}`} />
                    </div>
                    <span className={`text-sm font-medium ${scheduleType === 'custom' ? 'text-white' : 'text-gray-700'}`}>
                      Custom
                    </span>
                  </Button>
                </div>

                <div className="bg-gray-50 rounded-xl p-6 space-y-6">
                  {scheduleType === 'now' && (
                    <div className="space-y-4">
                      <div className="flex items-start gap-4">
                        <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                          <Share2 className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <h3 className="text-sm font-medium mb-1">Post Immediately</h3>
                          <p className="text-sm text-gray-500">Your content will be published as soon as possible</p>
                        </div>
                      </div>
                      {scheduleError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                          <p className="text-sm text-red-600">{scheduleError}</p>
                        </div>
                      )}
                      <Button 
                        className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg shadow-purple-500/25"
                        onClick={() => handlePost('now')}
                        disabled={isPosting || !selectedAccount}
                      >
                        {isPosting ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Publishing...
                          </>
                        ) : !selectedAccount ? (
                          'Select an Account'
                        ) : (
                          'Publish Now'
                        )}
                      </Button>
                    </div>
                  )}

                  {scheduleType === 'best-time' && (
                    <div className="space-y-4">
                      <div className="flex items-start gap-4">
                        <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                          <Clock3 className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <h3 className="text-sm font-medium mb-1">Optimal Posting Time</h3>
                          <p className="text-sm text-gray-500">We'll analyze your audience's activity and post when they're most engaged</p>
                        </div>
                      </div>
                      
                      <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center gap-3 text-sm">
                          {isFetchingBestTime ? (
                            <div className="flex items-center gap-2 text-gray-600">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span>Analyzing best posting time...</span>
                            </div>
                          ) : bestTime ? (
                            <>
                              <div className="flex items-center gap-2 text-purple-600 font-medium">
                                <Clock3 className="h-4 w-4" />
                                <span>{bestTime.toLocaleTimeString('en-US', { 
                                  hour: 'numeric',
                                  minute: 'numeric',
                                  hour12: true
                                })} Today</span>
                              </div>
                              <Badge className="bg-purple-100 text-purple-600 hover:bg-purple-200">
                                Recommended
                              </Badge>
                            </>
                          ) : scheduleError ? (
                            <span className="text-red-500">{scheduleError}</span>
                          ) : (
                            <span className="text-gray-500">No posting data available</span>
                          )}
                        </div>
                      </div>

                      <Button 
                        className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg shadow-purple-500/25"
                        onClick={() => handlePost('schedule', bestTime?.toISOString())}
                        disabled={isPosting || !bestTime}
                      >
                        {isPosting ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Scheduling...
                          </>
                        ) : (
                          'Schedule for Best Time'
                        )}
                      </Button>
                    </div>
                  )}

                  {scheduleType === 'custom' && (
                    <div className="space-y-4">
                      <div className="flex items-start gap-4">
                        <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                          <Calendar className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <h3 className="text-sm font-medium mb-1">Custom Schedule</h3>
                          <p className="text-sm text-gray-500">Choose your preferred date and time for posting</p>
                        </div>
                      </div>

                      <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <input
                          type="datetime-local"
                          className="w-full rounded-lg border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400"
                          min={new Date().toISOString().slice(0, 16)}
                          value={scheduledTime}
                          onChange={(e) => setScheduledTime(e.target.value)}
                        />
                      </div>

                      {scheduleError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                          <p className="text-sm text-red-600">{scheduleError}</p>
                        </div>
                      )}

                      <Button 
                        className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg shadow-purple-500/25"
                        onClick={() => handlePost('schedule', scheduledTime)}
                        disabled={isPosting || !scheduledTime}
                      >
                        {isPosting ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Scheduling...
                          </>
                        ) : (
                          'Schedule Post'
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {showProcessingModal && (
          <ProcessingModal
            downloadId={downloadId}
            onClose={() => {
              setShowProcessingModal(false);
              setDownloadId(null);
            }}
            user={user}
          />
        )}
      </div>
    </div>
  );
} 